import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, noop } from "../utils";
import { terminate, saveNodeType, isHoverForInteractiveType } from "./utils";

import DraggableOTOStopLine from "./components/DraggableOTOStopLine";
import DraggableOTOProfitLine from "./components/DraggableOTOProfitLine";
// NOTE: deltaTicks is now computed centrally in updateOTOPrice (useOrderOverlay)
// to avoid stale mainOrder.price issues during sequential drags.

class OTOOrdersInteractive extends Component {
  constructor(props) {
    super(props);

    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);
    this.terminate = terminate.bind(this);

    this.saveNodeType = saveNodeType.bind(this);
    this.getSelectionState = isHoverForInteractiveType("otoOrders").bind(this);

    this.nodes = [];
    this.state = {};
  }

  handleDrag(order, newPrice) {
    // debug removed

    const newOverride = {
      orderId: order.id,
      price: newPrice,
    };

    this.setState({
      override: newOverride,
    });

    // Call onDragStateChange to indicate OTO dragging is in progress
    // This prevents main order from responding to the same drag events
    const { onDragStateChange } = this.props;
    if (onDragStateChange) {
      onDragStateChange({
        type: "oto-drag",
        orderId: order.id,
        price: newPrice,
      });
    }
  }

  handleDragComplete(dragCompleteData) {
    // debug removed

    // Handle no-change completion: just clear override and drag state
    if (dragCompleteData && dragCompleteData.noChanges) {
      const { onDragStateChange } = this.props;
      if (onDragStateChange) onDragStateChange(null);
      this.setState({ override: null });
      return;
    }

    // Handle drag completion with simple price update (delta computed later)
    if (dragCompleteData && dragCompleteData.finalPrice !== undefined) {
      const { onUpdateOTOPrice, mainOrder } = this.props;
      const { order, finalPrice } = dragCompleteData;

      // debug removed

      if (onUpdateOTOPrice) {
        // Do NOT compute deltaTicks here – centralized in updateOTOPrice for fresh main price.
        onUpdateOTOPrice(mainOrder.id, order.id, finalPrice);
      }

      const { onDragStateChange } = this.props;
      if (onDragStateChange) {
        onDragStateChange(null);
      }

      this.setState({ override: null });
      return;
    }

    // Fallback to legacy override-based approach for backward compatibility
    const { override } = this.state;
    if (isDefined(override)) {
      const { onUpdateOTOPrice, mainOrder } = this.props;

      // debug removed

      // Update the OTO order price - pass mainOrderId, otoOrderId, newPrice
      if (onUpdateOTOPrice) {
        onUpdateOTOPrice(mainOrder.id, override.orderId, override.price);
      }

      // Clear override
      this.setState({
        override: null,
      });

      // Note: DO NOT call onDragStateChange for OTO drag completion
      // That callback is only for main order dragging, not OTO dragging
    }
  }

  render() {
    const {
      mainOrder,
      stopOrders = [],
      profitOrders = [],
      allOrders = [],
      offsetPixels,
      tempOTOPrices,
      onRemoveOTOOrder,
    } = this.props;
    const { override } = this.state;

    return (
      <g>
        {/* Render stop orders */}
        {stopOrders.map((stopOrder, idx) => {
          const key = `stop-${stopOrder.id}`;

          // Use override price during drag, otherwise preserve original order data including deltaTicks
          let orderProps = { ...stopOrder };
          if (override && override.orderId === stopOrder.id) {
            orderProps = { ...stopOrder, price: override.price };
          }
          // Note: When no override, we preserve deltaTicks and let getEffectivePrice() calculate the price

          return (
            <DraggableOTOStopLine
              key={key}
              ref={this.saveNodeType(`stop-${idx}`)}
              order={orderProps}
              mainOrder={mainOrder}
              symbol={mainOrder.symbol}
              allOrders={allOrders}
              offsetPixels={offsetPixels}
              tempOTOPrices={tempOTOPrices}
              onDrag={this.handleDrag}
              onDragComplete={this.handleDragComplete}
              onRemove={() => {
                // debug removed
                onRemoveOTOOrder && onRemoveOTOOrder(mainOrder.id, "ALL_STOPS");
              }}
            />
          );
        })}

        {/* Render profit orders */}
        {profitOrders.map((profitOrder, idx) => {
          const key = `profit-${profitOrder.id}`;

          // Use override price during drag, otherwise preserve original order data including deltaTicks
          let orderProps = { ...profitOrder };
          if (override && override.orderId === profitOrder.id) {
            orderProps = { ...profitOrder, price: override.price };
          }
          // Note: When no override, we preserve deltaTicks and let getEffectivePrice() calculate the price

          return (
            <DraggableOTOProfitLine
              key={key}
              ref={this.saveNodeType(`profit-${idx}`)}
              order={orderProps}
              mainOrder={mainOrder}
              symbol={mainOrder.symbol}
              allOrders={allOrders}
              offsetPixels={offsetPixels}
              tempOTOPrices={tempOTOPrices}
              onDrag={this.handleDrag}
              onDragComplete={this.handleDragComplete}
              onRemove={() => {
                // debug removed
                onRemoveOTOOrder && onRemoveOTOOrder(mainOrder.id, "ALL_STOPS");
              }}
            />
          );
        })}
      </g>
    );
  }
}

OTOOrdersInteractive.propTypes = {
  mainOrder: PropTypes.shape({
    id: PropTypes.string.isRequired,
    side: PropTypes.string.isRequired,
    quantity: PropTypes.number.isRequired,
    price: PropTypes.number.isRequired,
    state: PropTypes.string.isRequired,
    symbol: PropTypes.string.isRequired,
  }).isRequired,
  stopOrders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      side: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      price: PropTypes.number.isRequired,
      type: PropTypes.string.isRequired,
      symbol: PropTypes.string.isRequired,
    }),
  ),
  profitOrders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      side: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      price: PropTypes.number.isRequired,
      type: PropTypes.string.isRequired,
      symbol: PropTypes.string.isRequired,
    }),
  ),
  allOrders: PropTypes.array,
  offsetPixels: PropTypes.number,
  tempOTOPrices: PropTypes.object,
  onUpdateOTOPrice: PropTypes.func.isRequired,
  onRemoveOTOOrder: PropTypes.func,
  onDragStateChange: PropTypes.func,
};

OTOOrdersInteractive.defaultProps = {
  stopOrders: [],
  profitOrders: [],
  allOrders: [],
  offsetPixels: 0,
  tempOTOPrices: null,
  onRemoveOTOOrder: noop,
  onDragStateChange: noop,
};

OTOOrdersInteractive.contextTypes = {
  subscribe: PropTypes.func.isRequired,
  unsubscribe: PropTypes.func.isRequired,
  generateSubscriptionId: PropTypes.func.isRequired,
  chartId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default OTOOrdersInteractive;
